import {
  formatCents,
  formatCount,
  formatDateTime,
  formatTime as sharedFormatTime,
  parseMoneyToCents,
  toDateParam,
  tt,
  type MoneyInput,
} from '@agos/shared'

/**
 * Hours worked, and the two ways the payroll figure silently loses money.
 *
 * **FB-101.** An hourly rate is stored per employee id — `{"1": 0, "7": 5.5, "12": 5, …}` in
 * `restaurant_settings.hourly_rates` — and legacy reads it as `hourlyRates[row.id] ?? 0`. Three of the
 * thirteen employees with completed shifts have **no rate at all**, so 229,2 of the 5 851,8 recorded
 * hours are counted at zero euros and the *Výkaz miezd* total says nothing about it. Two more are
 * deliberately zero (the developer accounts), which is a different thing and has to read differently.
 *
 * **FB-102.** A shift with no check-out contributes nothing:
 *
 * ```tsx
 * if (r.checkIn && r.checkOut) {
 *   const ms = new Date(r.checkOut).getTime() - new Date(r.checkIn).getTime()
 *   if (ms > 0) { empMap[r.employeeId]!.totalMs += ms; empMap[r.employeeId]!.completeDays++ }
 * }
 * ```
 *
 * **55 of the 542 records have no check-out**, across 14 of the 17 people with any attendance at all.
 * Legacy's row shows `days` — which counts them — beside `totalMs`, which does not, so a month reads
 * as *"22 dní · 148h"* with four of those days worth nothing and no indication which.
 *
 * Both are the same class of defect: a figure somebody is paid from, quietly missing a component. So
 * the hours are separated into *counted* and *not counted*, every reason is named, and the payroll
 * total says what it excludes.
 *
 * Every record in the data is `type = 'manual'`, `hikvision_config` is empty and nobody carries a
 * card, so no punch has ever come from a terminal.
 */

export type AttendanceType = 'manual' | 'fingerprint' | 'chip' | 'rfid' | 'pin_code'

export interface AttendanceRecord {
  id?: number
  employeeId?: number
  workDate?: string
  checkIn?: string | null
  checkOut?: string | null
  type?: string
  deviceId?: string | null
  notes?: string | null
  employeeName?: string
  employeeRole?: string
  employeePosition?: string
  /**
   * When the employee last corrected this day's times from the sidebar - BACKEND.md §10A.414, FRONTEND.md §9.760.
   *
   * The newest `self_amend` row the audit log holds for this record, and null for a day its owner never corrected.
   * `GET /attendance` and `GET /attendance/monthly` answer it; it is read here, on the hand-written record, because
   * the generated client does not declare it until it is regenerated from the refreshed spec.
   */
  selfAmendedAt?: string | null
}

export interface StaffMember {
  id?: number
  name?: string
  systemRole?: string
  position?: string
  active?: boolean
}

/** Postgres prints `2026-07-29 08:15:04.379829+00`; `Date.parse` refuses it twice. */
export function parseStamp(value: string | null | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null
  const parsed = Date.parse(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  return Number.isFinite(parsed) ? parsed : null
}

// ─── one shift ───────────────────────────────────────────────────────────────

export type ShiftState = 'working' | 'finished' | 'open' | 'absent' | 'impossible'

/**
 * What state a day is in.
 *
 * `open` is the one legacy has no name for: a check-in with no check-out, on a day that has passed.
 * Somebody forgot, and until it is corrected the day is worth nothing. `working` is the same shape on
 * *today*, which is simply somebody at work.
 */
export function shiftState(record: AttendanceRecord | null, today: string): ShiftState {
  if (record === null) return 'absent'
  const inAt = parseStamp(record.checkIn)
  const outAt = parseStamp(record.checkOut)
  if (inAt === null) return 'absent'
  if (outAt === null) return (record.workDate ?? '') >= today ? 'working' : 'open'
  return outAt <= inAt ? 'impossible' : 'finished'
}

/** Milliseconds worked, or `null` when the day cannot say. */
export function workedMs(record: AttendanceRecord | null): number | null {
  if (record === null) return null
  const inAt = parseStamp(record.checkIn)
  const outAt = parseStamp(record.checkOut)
  if (inAt === null || outAt === null || outAt <= inAt) return null
  return outAt - inAt
}

/** `8h 05min`. Never a decimal — a shift is read off a clock. */
export function formatDuration(millis: number | null): string {
  if (millis === null || millis <= 0) return '-'
  const hours = Math.floor(millis / 3_600_000)
  const minutes = Math.floor((millis % 3_600_000) / 60_000)
  return `${hours}h ${String(minutes).padStart(2, '0')}min`
}

/**
 * A stamp as a clock time, or a dash.
 *
 * **§9.3.** This formatted with a bare `toLocaleTimeString('sk-SK', …)` — no zone — so it followed the
 * *device's* clock. On a tablet set to UTC the 22:09 check-out above reads 20:09, and on one set to New York
 * it reads 16:09: six hours of somebody's shift, on the page whose figures go into the payroll. Every
 * formatter in `@agos/shared` pins `Europe/Bratislava` for exactly this reason, and this now goes through
 * one of them. The `—` for a missing stamp is this module's own and is kept.
 */
export function formatTime(value: string | null | undefined): string {
  const stamp = parseStamp(value)
  if (stamp === null) return '-'
  return sharedFormatTime(stamp)
}

/** A shift longer than this is worth asking about before it is paid. */
export const IMPLAUSIBLE_HOURS = 16

export function isImplausible(record: AttendanceRecord): boolean {
  const worked = workedMs(record)
  return worked !== null && worked > IMPLAUSIBLE_HOURS * 3_600_000
}

// ─── today ───────────────────────────────────────────────────────────────────

export interface TodayRow extends StaffMember {
  record: AttendanceRecord | null
  state: ShiftState
}

const STATE_ORDER: Record<ShiftState, number> = {
  working: 0,
  open: 1,
  impossible: 2,
  finished: 3,
  absent: 4,
}

/**
 * Everybody active, with today's record beside them — at work first, then the days that need
 * attention, then the ones already done.
 *
 * Legacy sorts working / checked-out / absent and buries an open shift among the finished ones.
 * `GET /attendance/today-summary` does this join on the server and legacy calls neither it nor
 * anything like it; the join is kept here because the page also needs the states.
 */
export function todayRows(
  staff: StaffMember[],
  records: AttendanceRecord[],
  today: string,
): TodayRow[] {
  const byEmployee = new Map<number, AttendanceRecord>()
  for (const record of records) {
    if (record.employeeId !== undefined) byEmployee.set(record.employeeId, record)
  }

  return staff
    .filter((member) => member.active !== false)
    .map((member) => {
      const record = byEmployee.get(member.id ?? -1) ?? null
      return { ...member, record, state: shiftState(record, today) }
    })
    .sort(
      (left, right) =>
        STATE_ORDER[left.state] - STATE_ORDER[right.state] ||
        (left.name ?? '').localeCompare(right.name ?? ''),
    )
}

export interface TodayTotals {
  present: number
  finished: number
  absent: number
  /** Days somebody clocked in and never out — worth nothing until corrected. */
  open: number
}

export function todayTotals(rows: TodayRow[]): TodayTotals {
  let present = 0
  let finished = 0
  let absent = 0
  let open = 0
  for (const row of rows) {
    if (row.state === 'working') present += 1
    else if (row.state === 'finished') finished += 1
    else if (row.state === 'open' || row.state === 'impossible') open += 1
    else absent += 1
  }
  return { present, finished, absent, open }
}

// ─── the month, and the payroll ──────────────────────────────────────────────

export interface PayrollRow {
  employeeId: number
  name: string
  role: string
  position: string
  /** Days with a record of any kind. */
  days: number
  /** Days that produced hours. */
  paidDays: number
  /** Days recorded but worth nothing, and why — FB-102. */
  openDays: number
  impossibleDays: number
  countedMs: number
  /** What an hour is worth, in cents; `null` when nobody has set one — FB-101. */
  rateCents: number | null
  /** True when the rate is set and is deliberately nought. */
  unpaidByDesign: boolean
  earnedCents: number
  records: AttendanceRecord[]
}

export type RateMap = Record<string, MoneyInput>

/**
 * The rate for one employee.
 *
 * Keyed by **employee id**, which is what `restaurant_settings.hourly_rates` holds —
 * `{"1": 0, "7": 5.5, …}`. The endpoint's own summary says *"What each role is paid per hour"*, which
 * is wrong and is filed: a client that believed it would pay everybody by role.
 *
 * `null` means nobody has set one, which is not the same as nought and must not read the same.
 */
export function rateCentsFor(employeeId: number, rates: RateMap): number | null {
  const raw = rates[String(employeeId)]
  if (raw === undefined || raw === null || raw === '') return null
  return parseMoneyToCents(raw)
}

/**
 * What each employee worked and is owed for a month.
 *
 * Everybody active appears, whether or not they have a record, so a missing month is visible rather
 * than absent. Earnings are computed in cents from the hours — a rate is a multiplicand and keeps its
 * decimals (FB-056); only the product becomes money.
 */
export function payrollRows(
  records: AttendanceRecord[],
  staff: StaffMember[],
  rates: RateMap,
  today: string,
): PayrollRow[] {
  const rows = new Map<number, PayrollRow>()

  const blank = (employeeId: number, member: Partial<PayrollRow>): PayrollRow => ({
    employeeId,
    name: member.name ?? '',
    role: member.role ?? '',
    position: member.position ?? '',
    days: 0,
    paidDays: 0,
    openDays: 0,
    impossibleDays: 0,
    countedMs: 0,
    rateCents: rateCentsFor(employeeId, rates),
    unpaidByDesign: rateCentsFor(employeeId, rates) === 0,
    earnedCents: 0,
    records: [],
  })

  for (const record of records) {
    const employeeId = record.employeeId
    if (employeeId === undefined) continue
    const row =
      rows.get(employeeId) ??
      blank(employeeId, {
        name: record.employeeName ?? '',
        role: record.employeeRole ?? '',
        position: record.employeePosition ?? '',
      })
    row.days += 1
    row.records.push(record)
    const state = shiftState(record, today)
    const worked = workedMs(record)
    if (worked !== null) {
      row.countedMs += worked
      row.paidDays += 1
    } else if (state === 'impossible') {
      row.impossibleDays += 1
    } else if (state === 'open' || state === 'working') {
      row.openDays += 1
    }
    rows.set(employeeId, row)
  }

  for (const member of staff) {
    if (member.active === false || member.id === undefined) continue
    if (!rows.has(member.id)) {
      rows.set(
        member.id,
        blank(member.id, {
          name: member.name ?? '',
          role: member.systemRole ?? '',
          position: member.position ?? '',
        }),
      )
    }
  }

  for (const row of rows.values()) {
    if (row.rateCents !== null) {
      row.earnedCents = Math.round((row.countedMs / 3_600_000) * row.rateCents)
    }
  }

  return [...rows.values()].sort((left, right) => left.name.localeCompare(right.name))
}

export interface PayrollTotals {
  countedMs: number
  earnedCents: number
  /** Rows the total could not price, because nobody set a rate — FB-101. */
  withoutRate: number
  msWithoutRate: number
  /** Days recorded that produced no hours — FB-102. */
  openDays: number
  impossibleDays: number
  /** Rows whose rate is deliberately nought. */
  unpaidByDesign: number
}

/**
 * The month's total, with what it could not include stated beside it.
 *
 * Legacy's total is `sum(hours × (rate ?? 0))` and nothing else. Two components go missing without a
 * word: hours belonging to somebody with no rate, and days with no check-out. Both are counted here so
 * the figure can be read for what it is.
 */
export function payrollTotals(rows: PayrollRow[]): PayrollTotals {
  let countedMs = 0
  let earnedCents = 0
  let withoutRate = 0
  let msWithoutRate = 0
  let openDays = 0
  let impossibleDays = 0
  let unpaidByDesign = 0

  for (const row of rows) {
    countedMs += row.countedMs
    earnedCents += row.earnedCents
    openDays += row.openDays
    impossibleDays += row.impossibleDays
    if (row.rateCents === null) {
      if (row.countedMs > 0) {
        withoutRate += 1
        msWithoutRate += row.countedMs
      }
    } else if (row.rateCents === 0 && row.countedMs > 0) {
      unpaidByDesign += 1
    }
  }

  return {
    countedMs,
    earnedCents,
    withoutRate,
    msWithoutRate,
    openDays,
    impossibleDays,
    unpaidByDesign,
  }
}

/** Whether the total is the whole story. */
export function payrollIsComplete(totals: PayrollTotals): boolean {
  return totals.withoutRate === 0 && totals.openDays === 0 && totals.impossibleDays === 0
}

/**
 * Whether this employee did anything at all in the month — §9.261, owner O12.
 *
 * `payrollRows` deliberately gives **everybody active** a row, whether or not they have a record, so
 * that a month somebody was expected to work and did not is visible rather than absent. That is right
 * for the wage sheet and wrong for the monthly overview, where a restaurant with a long staff list
 * reads as a wall of empty strips: the owner's words were *"you will not see in mesačný prehľad that
 * user (basically he did not worked)"*.
 *
 * `days` rather than `countedMs`, and the difference matters: somebody who clocked in and never
 * clocked out has no hours and **did** work, and hiding them would hide the very row that needs
 * fixing.
 */
export function hasActivity(row: PayrollRow): boolean {
  return row.days > 0
}

/** The overview's rows with the empty ones dropped, and how many were dropped. */
export function withoutIdleRows(rows: PayrollRow[]): { rows: PayrollRow[]; hidden: number } {
  const kept = rows.filter(hasActivity)
  return { rows: kept, hidden: rows.length - kept.length }
}

// ─── the month grid ──────────────────────────────────────────────────────────

export interface MonthCell {
  date: string
  day: number
  weekday: number
  record: AttendanceRecord | null
  state: ShiftState
  ms: number | null
}

/** `2026-07` → every day of it, with whatever was recorded. */
export function monthCells(
  year: number,
  month: number,
  records: AttendanceRecord[],
  today: string,
): MonthCell[] {
  const byDate = new Map<string, AttendanceRecord>()
  for (const record of records) {
    if (record.workDate !== undefined) byDate.set(record.workDate, record)
  }

  const days = new Date(year, month, 0).getDate()
  const cells: MonthCell[] = []
  for (let day = 1; day <= days; day += 1) {
    const date = `${year}-${pad(month)}-${pad(day)}`
    const record = byDate.get(date) ?? null
    cells.push({
      date,
      day,
      weekday: new Date(year, month - 1, day).getDay(),
      record,
      state: shiftState(record, today),
      ms: workedMs(record),
    })
  }
  return cells
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

export function monthLabel(year: number, month: number): string {
  const names = [
    tt('Január'),
    tt('Február'),
    tt('Marec'),
    tt('Apríl'),
    tt('Máj'),
    tt('Jún'),
    tt('Júl'),
    tt('August'),
    tt('September'),
    tt('Október'),
    tt('November'),
    tt('December'),
  ]
  return `${names[month - 1] ?? ''} ${year}`
}

export function shiftMonth(year: number, month: number, step: number): { year: number; month: number } {
  const shifted = new Date(year, month - 1 + step, 1)
  return { year: shifted.getFullYear(), month: shifted.getMonth() + 1 }
}

// ─── the correction form ─────────────────────────────────────────────────────

export interface ShiftForm {
  /** `HH:MM`, local, as the clock on the wall reads. */
  checkIn: string
  checkOut: string
  notes: string
}

export function shiftFormOf(record: AttendanceRecord | null): ShiftForm {
  return {
    checkIn: timeOf(record?.checkIn),
    checkOut: timeOf(record?.checkOut),
    notes: record?.notes ?? '',
  }
}

function timeOf(value: string | null | undefined): string {
  const stamp = parseStamp(value)
  if (stamp === null) return ''
  const date = new Date(stamp)
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export type ShiftProblem =
  | { kind: 'no-times' }
  | { kind: 'out-before-in' }
  | { kind: 'out-without-in' }
  | { kind: 'implausible'; hours: number }
  | { kind: 'in-the-future' }

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/

/**
 * What is wrong with a correction.
 *
 * Legacy checks nothing. Its own save builds both stamps from the same `workDate`, so a check-out
 * earlier than a check-in is accepted, stored, and then contributes **zero hours** for ever — a third
 * silent zero on top of FB-101 and FB-102. No record in the data has it yet.
 *
 * An overnight shift is the legitimate case that looks the same, so it is offered explicitly rather
 * than guessed at: *ends the next day*.
 */
export function shiftProblems(form: ShiftForm, overnight: boolean, nowMinutes: number | null): ShiftProblem[] {
  const problems: ShiftProblem[] = []
  const inAt = minutesOf(form.checkIn)
  const outAt = minutesOf(form.checkOut)

  if (inAt === null && outAt === null) {
    problems.push({ kind: 'no-times' })
    return problems
  }
  if (inAt === null && outAt !== null) problems.push({ kind: 'out-without-in' })

  if (inAt !== null && outAt !== null) {
    const span = overnight ? outAt + 24 * 60 - inAt : outAt - inAt
    if (span <= 0) problems.push({ kind: 'out-before-in' })
    else if (span > IMPLAUSIBLE_HOURS * 60) {
      problems.push({ kind: 'implausible', hours: Math.round((span / 60) * 10) / 10 })
    }
  }

  if (nowMinutes !== null) {
    const latest = Math.max(inAt ?? -1, overnight ? -1 : (outAt ?? -1))
    if (latest > nowMinutes) problems.push({ kind: 'in-the-future' })
  }

  return problems
}

function minutesOf(time: string): number | null {
  const match = TIME_PATTERN.exec(time.trim())
  if (match === null) return null
  return Number(match[1]) * 60 + Number(match[2])
}

/** An implausible span is questioned, not refused. Everything else blocks the save. */
export function canSaveShift(problems: ShiftProblem[]): boolean {
  return !problems.some((problem) => problem.kind !== 'implausible')
}

/**
 * What each problem says - one place for both forms that name them, FRONTEND.md §9.760.
 *
 * Moved here from `AttendancePage`'s correction dialog, word for word, so the sidebar's *Upraviť časy zmeny* says
 * the manager's sentences rather than a second copy of them that would drift.
 *
 * `overnightOffered` is whether the form beside the sentence has the *na druhý deň* box. The manager's has it, and
 * its sentence for a departure before the arrival ends by telling the reader to tick it. The sidebar's has none - a
 * departure after midnight is in the future while today's record is the current one, BACKEND.md §10A.414 - so there
 * the sentence stops before an instruction about a box that is not on the screen. The manager's sentence is kept as
 * the one string it was, so the translations it already has are the ones it keeps.
 *
 * A `switch` with no `default`, so a sixth problem is TS2366 here rather than a sentence nobody wrote.
 */
export function shiftProblemText(problem: ShiftProblem, overnightOffered: boolean): string {
  switch (problem.kind) {
    case 'no-times':
      return tt('Zadajte aspoň príchod.')
    case 'out-without-in':
      return tt('Odchod bez príchodu nedáva hodiny.')
    case 'out-before-in':
      return overnightOffered
        ? tt('Odchod je pred príchodom. Ak išlo o nočnú zmenu, zaškrtnite „na druhý deň“.')
        : tt('Odchod je pred príchodom.')
    case 'in-the-future':
      return tt('Ten čas ešte nenastal.')
    case 'implausible':
      return `${tt('Zmena dlhá')} ${problem.hours} ${tt('hodín - je to naozaj tak?')}`
  }
}

/**
 * The body the endpoint wants.
 *
 * `HH:MM` is a wall-clock time on `workDate`, so it becomes a local `Date` and then an instant — the
 * same conversion legacy does, and the right one: 08:00 means eight o'clock where the restaurant is.
 * An overnight check-out lands on the following day, which legacy cannot express at all.
 */
export function shiftPayload(
  workDate: string,
  employeeId: number,
  form: ShiftForm,
  overnight: boolean,
): Record<string, unknown> {
  const payload: Record<string, unknown> = { employeeId, workDate, type: 'manual' }
  if (form.checkIn.trim() !== '') {
    payload.checkIn = instantOf(workDate, form.checkIn, 0)
  }
  if (form.checkOut.trim() !== '') {
    payload.checkOut = instantOf(workDate, form.checkOut, overnight ? 1 : 0)
  }
  payload.notes = form.notes.trim() || null
  return payload
}

function instantOf(workDate: string, time: string, plusDays: number): string {
  const date = new Date(`${workDate}T${time}:00`)
  date.setDate(date.getDate() + plusDays)
  return date.toISOString()
}

/** Whether this correction crosses midnight, from the record as stored. */
export function looksOvernight(record: AttendanceRecord | null): boolean {
  const inAt = parseStamp(record?.checkIn)
  const outAt = parseStamp(record?.checkOut)
  if (inAt === null || outAt === null) return false
  return new Date(outAt).getDate() !== new Date(inAt).getDate()
}

// ─── correcting your own day, from the sidebar - §9.760 ─────────────────────

/**
 * What `PATCH /attendance/self/today` is sent - BACKEND.md §10A.414.
 *
 * A type rather than an interface because the generated body is `{ [key: string]: unknown }`, and only a type
 * alias gets the implicit index signature that lets this be handed to the generated hook unchanged.
 */
export type SelfAmendBody = {
  checkIn: string | null
  checkOut: string | null
  notes: string | null
}

/**
 * Whether the sidebar's departure is on the day after its arrival - FRONTEND.md §9.761, BACKEND.md §10A.415.
 *
 * **Only for a shift carried over from yesterday, and then chosen by the times** rather than by a tick box: a
 * carried-over shift is the only kind the sidebar holds that can end on the next day, so a departure earlier than its
 * arrival can mean nothing else. Today's shift never does - a departure after midnight is in the future while today's
 * record is the current one (§10A.414) - so there a departure before the arrival stays before it, for
 * `shiftProblems` to name and the server to refuse (`ATTENDANCE_OUT_BEFORE_IN`).
 *
 * **Strictly earlier.** A departure at the arrival's own minute is a shift of nought hours rather than one of
 * twenty-four, so it stays the problem it is on the manager's form.
 */
export function selfEndsNextDay(form: ShiftForm, carriedOver: boolean): boolean {
  if (!carriedOver) return false
  const inAt = minutesOf(form.checkIn)
  const outAt = minutesOf(form.checkOut)
  return inAt !== null && outAt !== null && outAt < inAt
}

/**
 * The sidebar's correction as the self endpoint wants it - FRONTEND.md §9.760, §9.761.
 *
 * Each time goes through the conversion the manager's form makes (`instantOf`): a wall-clock `HH:MM` on the work
 * date, read as local and sent as an instant. The departure lands on the next day exactly when `selfEndsNextDay`
 * says so - the manager's *na druhý deň* conversion - and otherwise both times are on `workDate`.
 *
 * `carriedOver` is required rather than defaulted, so a call site that forgets which shift it is correcting does not
 * compile rather than quietly sending yesterday's departure on yesterday's date (§9.480's argument).
 *
 * All three keys every time, which is the shape the dialog promises:
 * - an empty departure is **null** - it re-opens a shift closed by mistake, which the server keeps allowed;
 * - an empty note is null rather than an empty string, as the manager's form sends it;
 * - an empty arrival is null too. The dialog never sends one, because `shiftProblems` holds the save, and the server
 *   refuses it if anything else does (`ATTENDANCE_ARRIVAL_REQUIRED`).
 *
 * A time that is not `HH:MM` counts as empty, which is how `shiftProblems` reads it - converting it would hand
 * `toISOString` an invalid date, and that throws.
 */
export function selfAmendPayload(workDate: string, form: ShiftForm, carriedOver: boolean): SelfAmendBody {
  const nextDay = selfEndsNextDay(form, carriedOver)
  return {
    checkIn: minutesOf(form.checkIn) === null ? null : instantOf(workDate, form.checkIn.trim(), 0),
    checkOut: minutesOf(form.checkOut) === null ? null : instantOf(workDate, form.checkOut.trim(), nextDay ? 1 : 0),
    notes: form.notes.trim() || null,
  }
}

/**
 * The clock the sidebar's correction checks a time in the future against - FRONTEND.md §9.760.
 *
 * Minutes since the device's midnight while the device's calendar day is the record's work date, and null on any
 * other day: `shiftProblems` compares times of day, which say nothing across two dates. The manager's form hands it
 * its clock under the same condition (`editing.date === today`). The server checks the instant itself, allowing five
 * minutes for a clock that runs ahead (BACKEND.md §10A.414), so this only saves a refused request.
 */
export function selfNowMinutes(workDate: string, now: Date): number | null {
  if (toDateParam(now) !== workDate) return null
  return now.getHours() * 60 + now.getMinutes()
}

/**
 * What is wrong with the sidebar's correction - the manager's rule, FRONTEND.md §9.760 and §9.761.
 *
 * **Today's shift** is exactly `shiftProblems(form, false, nowMinutes)`: no overnight, and the clock compared while
 * the device's day is the work date.
 *
 * **A shift carried over from yesterday** takes the overnight its times choose (`selfEndsNextDay`), and its future
 * check is made **across midnight**: a time of day cannot do it, because a departure at 02:30 is earlier than an
 * arrival at 18:00 and is still in the future at 01:00. So `shiftProblems` is asked with no clock, and the two
 * instants the payload would send are compared with `now` instead - the same instants, so what this calls in the
 * future is exactly what the server would refuse as `ATTENDANCE_IN_THE_FUTURE`, less its five minutes of grace.
 */
export function selfShiftProblems(
  form: ShiftForm,
  workDate: string,
  carriedOver: boolean,
  now: Date,
): ShiftProblem[] {
  if (!carriedOver) return shiftProblems(form, false, selfNowMinutes(workDate, now))
  const problems = shiftProblems(form, selfEndsNextDay(form, true), null)
  const body = selfAmendPayload(workDate, form, true)
  const latest = Math.max(parseStamp(body.checkIn) ?? -Infinity, parseStamp(body.checkOut) ?? -Infinity)
  if (latest > now.getTime()) problems.push({ kind: 'in-the-future' })
  return problems
}

/**
 * A work date as it is said - `2026-10-08` is *8. 10. 2026* - FRONTEND.md §9.760.
 *
 * Read off the string rather than through a `Date`, so no zone can move the day; anything that is not a date comes
 * back as itself, as `sheetDate` does, because a wrong date on screen must be visible rather than blank.
 */
export function workDayLabel(workDate: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(workDate)
  if (parts === null) return workDate
  return `${Number(parts[3])}. ${Number(parts[2])}. ${parts[1]}`
}

/**
 * The day under the dialog's heading - *Dnes, 8. 10. 2026*, or *Včera, 7. 10. 2026* for a shift carried over from
 * yesterday - FRONTEND.md §9.760, §9.761. Said in words as well as numbers, because the person reading it at 01:00
 * is the one most likely to think it is still the day they started on.
 */
export function selfShiftDay(workDate: string, carriedOver: boolean): string {
  return `${carriedOver ? tt('Včera') : tt('Dnes')}, ${workDayLabel(workDate)}`
}

/**
 * The tooltip on a day its owner corrected, or null for a day nobody did - FRONTEND.md §9.760.
 *
 * When, in the restaurant's zone, and where the times before it are: the correction writes them to the audit log
 * (BACKEND.md §10A.414) and this screen shows only the times as they are now. A stamp that will not parse still
 * marks the day - the correction happened whatever its time reads as - and the note says so without a time.
 */
export function selfAmendedNote(record: AttendanceRecord | null | undefined): string | null {
  const amendedAt = record?.selfAmendedAt
  if (typeof amendedAt !== 'string' || amendedAt.trim() === '') return null
  const stamp = parseStamp(amendedAt)
  const when = stamp === null ? '' : ` ${formatDateTime(stamp)}`
  return `${tt('Upravené zamestnancom')}${when}. ${tt('Pôvodné časy sú v audit logu.')}`
}

// ─── the terminal ────────────────────────────────────────────────────────────

export interface TerminalConfig {
  ip?: string | null
  lastSyncAt?: string | null
  lastSyncCount?: number | null
}

export type TerminalState = 'absent' | 'never-synced' | 'synced'

/**
 * Whether the attendance terminal exists.
 *
 * `hikvision_config` is empty in the restored data, nobody carries a card, and all 542 records are
 * `type = 'manual'`. So every hour on this screen was typed by a person, and the page says so rather
 * than showing a *Fingerprint* icon that has never fired.
 */
export function terminalState(config: TerminalConfig | undefined): TerminalState {
  if ((config?.ip ?? '') === '') return 'absent'
  return (config?.lastSyncAt ?? null) === null ? 'never-synced' : 'synced'
}

const TYPE_LABELS: Record<AttendanceType, string> = {
  manual: tt('ručne'),
  fingerprint: tt('odtlačok'),
  chip: tt('čip'),
  rfid: 'RFID',
  pin_code: tt('PIN'),
}

export function typeLabel(type: string | undefined): string {
  return TYPE_LABELS[(type ?? 'manual') as AttendanceType] ?? (type ?? '')
}

/** How many records came from a device rather than from somebody typing. */
export function deviceRecordCount(records: AttendanceRecord[]): number {
  return records.filter((record) => (record.type ?? 'manual') !== 'manual').length
}

// ─── filters ─────────────────────────────────────────────────────────────────

export function matchesStaffSearch(row: { name?: string; position?: string }, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return [row.name, row.position].some((field) =>
    String(field ?? '').toLowerCase().includes(needle),
  )
}

// `formatHours` was here and is deleted — §9.35, 2026-08-06. It returned `formatDuration(millis)` and
// nothing else: a second name for one behaviour, with no caller and no test.
//
// The simplest kind on this list, and worth removing rather than ignoring. `formatDuration` has ten
// callers; a synonym beside it is a fork waiting to happen — the day somebody changes how hours read,
// they change one of the two and the other keeps its own opinion.

export { formatCents, formatCount }

/**
 * Slovak day names, short — the payslip's date column reads *Po 04.08.*
 *
 * Named here and exempted rather than sent through `tt()`: these are date formatting, not copy. A
 * translation table keyed by *Št* would be a table of one language's abbreviations translated into
 * itself, and the sheet is a Slovak payroll document either way.
 */
// eslint-disable-next-line no-restricted-syntax -- date abbreviations, not translatable copy.
const SHEET_DAYS = ['Ne', 'Po', 'Ut', 'St', 'Št', 'Pi', 'So']

/**
 * A record's date as the printed sheet shows it — §9.184.
 *
 * Day name and `dd.mm.`, which is legacy's format and the one a signature sheet needs: somebody
 * checking their own hours reads down the weekdays, not the ISO dates.
 *
 * Parsed at midnight local (`T00:00:00`) rather than as a bare date, which JavaScript reads as UTC
 * and shifts a day backwards for anybody east of Greenwich — this franchise included.
 */
export function sheetDate(workDate: string | null | undefined): string {
  if (!workDate) return ''
  const parsed = new Date(`${workDate}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return workDate
  const day = String(parsed.getDate()).padStart(2, '0')
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  return `${SHEET_DAYS[parsed.getDay()] ?? ''} ${day}.${month}.`
}
